import { Injectable } from '@angular/core';
import {
  AggregatedSlotDto,
  CourtStateDto,
  DayAggregatedSlotsDto,
  isBlockedCourtState,
} from '../../../service/court-availability-service';
import {
  DayGridModel,
  CourtColumnModel,
  CourtColumnSegment,
  CourtDurationOption,
} from './court-grid.model';
import { hasOverlapWithBlockedRanges } from './court-grid-blocked-ranges.util';
import { addMinutesIso, findSegmentAt } from './court-grid-time.util';
import { VenueTimezoneService } from '../../../service/venue-timezone.service';

@Injectable({ providedIn: 'root' })
export class CourtGridDataService {
  constructor(private readonly venueTimezone: VenueTimezoneService) {}

  /**
   * Single pivot entry point for both Tagesansicht (days.length === 1) and
   * Wochenansicht (days.length === 7). Turns time-indexed AggregatedSlotDto[]
   * per day into court-indexed, run-length-merged DayGridModels.
   */
  buildWeekGrid(days: DayAggregatedSlotsDto[], intervalMinutes: number): DayGridModel[] {
    const referenceDay = days.find(d => d.slots.length > 0);
    if (!referenceDay) {
      return [];
    }

    // Derived once from the first non-empty day and applied to every day so
    // court order stays identical across all columns of the week.
    const courtOrder = referenceDay.slots[0].courts.breakdown.map(c => ({
      courtId: c.id,
      courtName: c.name,
    }));

    // "Today" in the VENUE's calendar, not the browser's: the day dates in
    // this grid are venue-calendar dates (the backend generates each day
    // window in the organization's timezone), so a browser-local today
    // (formatLocalISODate(new Date())) flags the WRONG day around midnight
    // whenever the user's timezone differs from the venue's — mis-placing
    // the "heute" badge, the now-line, and the autoscroll anchor. Computed
    // once per pivot; booking-container sets the venue timezone from the
    // API response before every rebuild. See docs/TIMEZONE_HANDLING.md.
    const venueToday = this.venueTimezone.getTodayDateString();

    return days.map(day => this.buildDayGrid(day.date, day.slots, intervalMinutes, courtOrder, venueToday));
  }

  /**
   * For a selected court + day + start time, determines which durations are
   * feasible by checking the column's precomputed blocked ranges and the
   * backend-provided pricing on the anchor slot.
   */
  getAvailableDurationsForCourt(
    courtId: string,
    dateStr: string,
    startTimeUtc: string,
    days: DayGridModel[],
    allDurations: number[],
  ): CourtDurationOption[] {
    const day = days.find(d => d.date === dateStr);
    const column = day?.courts.find(c => c.courtId === courtId);
    if (!day || !column) {
      return allDurations.map(d => ({ duration: d, available: false, endTimeUtc: '', price: 0 }));
    }

    const startMs = new Date(startTimeUtc).getTime();
    const anchorSlot = findSegmentAt(column.segments, startMs)?.aggregatedSlot;
    const dayEndMs = new Date(column.dayEndUtc).getTime();

    return allDurations.map(duration => {
      const endMs = startMs + duration * 60_000;
      const withinDay = !column.dayEndUtc || endMs <= dayEndMs;
      const free = withinDay && !hasOverlapWithBlockedRanges(startMs, endMs, column.blockedRanges);
      const durationOption = anchorSlot?.booking?.durations?.find(d => d.duration === duration);

      return {
        duration,
        available: free && !!durationOption,
        endTimeUtc: durationOption?.endTime ?? new Date(endMs).toISOString(),
        price: durationOption?.price ?? 0,
      };
    });
  }

  private buildDayGrid(
    date: string,
    slots: AggregatedSlotDto[],
    intervalMinutes: number,
    courtOrder: { courtId: string; courtName: string }[],
    venueToday: string,
  ): DayGridModel {
    const isToday = venueToday === date;

    if (!slots.length) {
      return {
        date,
        isToday,
        timeColumns: [],
        courts: courtOrder.map(({ courtId, courtName }) => ({
          courtId,
          courtName,
          segments: [],
          blockedRanges: [],
          dayStartUtc: '',
          dayEndUtc: '',
        })),
      };
    }

    const timeColumns = slots.map(s => s.startTimeUtc).sort();
    const slotByTime = new Map(slots.map(s => [s.startTimeUtc, s]));
    const dayStartUtc = timeColumns[0];
    const lastTime = timeColumns[timeColumns.length - 1];
    const lastSlot = slotByTime.get(lastTime)!;
    const dayEndUtc = lastSlot.latestEndTimeUtc ?? addMinutesIso(lastTime, intervalMinutes);

    const courts: CourtColumnModel[] = courtOrder.map(({ courtId, courtName }) => {
      const segments: CourtColumnSegment[] = [];

      for (const time of timeColumns) {
        const slot = slotByTime.get(time)!;
        const courtState = slot.courts.breakdown.find(c => c.id === courtId);
        const kind = this.resolveKind(slot, courtState);
        const occupancyId =
          kind === 'occupied' || kind === 'course' || kind === 'blocked'
            ? courtState?.occupancyId
            : undefined;
        const endTimeUtc = addMinutesIso(time, intervalMinutes);
        // (kind, eventType, displayName) drive the plan §6.4.3 tile label. A
        // 'course' segment reads them off its session payload; a 'blocked'
        // one has no payload by design and reads them off the court state.
        const eventType =
          kind === 'course'
            ? courtState?.courseSession?.eventType ?? null
            : kind === 'blocked'
              ? courtState?.eventType ?? null
              : undefined;
        const displayName =
          kind === 'course'
            ? courtState?.courseSession?.displayName
            : kind === 'blocked'
              ? courtState?.displayName
              : undefined;

        // Everything the tile actually RENDERS, hoisted above the merge
        // decision because the id-less fallback below keys on it.
        const bookingType = kind === 'occupied' ? courtState?.bookingType : undefined;
        const blockedReason = courtState?.blockedReason;
        const isOwn =
          (kind === 'occupied' && courtState?.isOwnBooking) ||
          (kind === 'course' && courtState?.courseSession?.isUserEnrolled) ||
          undefined;

        // Merge into the previous segment only when it's unambiguously the
        // SAME occupancy continuing: 'available' never merges (pricing can
        // vary by start time — getAvailableDurationsForCourt()'s anchor
        // lookup is only accurate for the first raw slot in a merged run,
        // see getAvailableDurationsForCourt), 'past' merges freely (it's a
        // time cutoff, not a booking), and everything else merges on
        // occupancyId — state/bookingType alone can't tell "one booking
        // spanning two intervals" apart from "two different, back-to-back
        // bookings of the same type".
        //
        // THE ID-LESS FALLBACK. The backend withholds occupancyId for exactly
        // two occupancies (see CourtColumnSegment.occupancyId): a private
        // session and a session of a deactivated course. Both arrive as
        // kind 'occupied' with NO name, NO eventType, NO bookingType and NO
        // ownership flag, so a 60-minute private session would otherwise
        // render as two adjacent 30-minute "Belegt" tiles. It also covers a
        // hypothetical closure payload with no id ('blocked').
        //
        // The key is "same court, adjacent in time, and IDENTICAL in every
        // field the tile renders" — bookingType, blockedReason, eventType,
        // displayName and the own-block flag, i.e. the full input set of
        // court-segment-label.util (class, label, tooltip, badge).
        //
        // Its one residual: two DIFFERENT id-less occupancies that render
        // identically (two back-to-back private sessions) merge into one bar.
        // That is unavoidable — the payload deliberately carries nothing that
        // separates them — and it is not a correctness loss: the merged bar
        // asserts only "this court is occupied 10:00–12:00", which is true,
        // it stays non-interactive, carries no payload, and covers the exact
        // same blockedRanges. What the key DOES prevent is merging things
        // that render differently: a private session never merges with an
        // adjacent id-less individual booking (different blockedReason /
        // bookingType) or with a differently-named closure.
        const last = segments[segments.length - 1];
        const contiguous =
          !!last &&
          last.kind === kind &&
          new Date(last.endTimeUtc).getTime() === new Date(time).getTime();
        const sameOccupancy = occupancyId != null && last?.occupancyId === occupancyId;
        const sameIdlessPresentation =
          occupancyId == null &&
          last?.occupancyId == null &&
          last?.bookingType === bookingType &&
          last?.blockedReason === blockedReason &&
          (last?.eventType ?? null) === (eventType ?? null) &&
          last?.displayName === displayName &&
          (last?.isOwn ?? false) === (isOwn ?? false);
        const canExtend =
          contiguous &&
          (kind === 'past' ||
            sameOccupancy ||
            ((kind === 'occupied' || kind === 'blocked') && sameIdlessPresentation));

        if (canExtend) {
          last!.endTimeUtc = endTimeUtc;
          // AND-fold: a merged occupied/course/blocked block is "past" only if
          // EVERY interval it covers is past — a booking spanning "now"
          // (earlier intervals past, later ones not) must render un-dimmed.
          if (kind === 'occupied' || kind === 'course' || kind === 'blocked') {
            last!.isPast = !!last!.isPast && slot.isPast;
          }
        } else {
          segments.push({
            startTimeUtc: time,
            endTimeUtc,
            kind,
            bookingType,
            occupancyId,
            // Carry the full session breakdown for course blocks — the column
            // renders the course name / enrolled badge from it and emits it on
            // click. A merged run keeps this FIRST interval's object (all
            // intervals of one occupancyId describe the same session).
            // NEVER set for 'blocked': a Platzsperre has no joinable payload,
            // and attaching one would make the tile clickable.
            courseSession: kind === 'course' ? courtState?.courseSession : undefined,
            courseKind:
              kind === 'blocked'
                ? 'block'
                : kind === 'course'
                  ? courtState?.courseSession?.kind ?? 'course'
                  : undefined,
            eventType,
            displayName,
            // Unified own-block flag (see model doc). Merged runs keep this
            // FIRST interval's value — same occupancyId, same owner (and the
            // id-less fallback only merges intervals whose flag is equal).
            isOwn,
            // isPast only carries meaning for occupied/course/blocked (see
            // model doc); kind 'past' is inherently past and keeps its own styling.
            isPast:
              kind === 'occupied' || kind === 'course' || kind === 'blocked'
                ? slot.isPast
                : undefined,
            blockedReason,
            aggregatedSlot: slot,
          });
        }
      }

      const blockedRanges = segments
        .filter(s => s.kind !== 'available')
        .map(s => ({
          startMs: new Date(s.startTimeUtc).getTime(),
          endMs: new Date(s.endTimeUtc).getTime(),
        }))
        .sort((a, b) => a.startMs - b.startMs);

      return { courtId, courtName, segments, blockedRanges, dayStartUtc, dayEndUtc };
    });

    return { date, isToday, courts, timeColumns };
  }

  /**
   * Resolves the segment kind from the per-court state FIRST — past intervals
   * that carry a booking/course keep their real kind so the grid still shows
   * the booking (dimmed via CourtColumnSegment.isPast). 'past' is only the
   * fallback for past intervals with nothing to show: available ones, or ones
   * missing a per-court breakdown entry entirely (no booking info to render).
   */
  private resolveKind(
    slot: AggregatedSlotDto,
    courtState: CourtStateDto | undefined,
  ): CourtColumnSegment['kind'] {
    if (!courtState) return slot.isPast ? 'past' : 'occupied';

    switch (courtState.state) {
      case 'available':
        return slot.isPast ? 'past' : 'available';
      case 'course':
        return 'course';
      case 'booked':
        // A Platzsperre arrives as an occupied court with a suppressed
        // payload (plan §6.3.4) — it must read as CLOSED, not as "someone
        // booked this". Everything else on 'booked' stays 'occupied'.
        return isBlockedCourtState(courtState) ? 'blocked' : 'occupied';
      case 'reserved':
      default:
        // Fail closed: an unknown state a stale frontend has never heard of
        // renders as occupied, never as free.
        return 'occupied';
    }
  }

}
